import { dispatchActivationProgressEvent } from "@glide/common-core/dist/js/activation";
import { isLoadingValue } from "@glide/computation-model-types";
import { asRow } from "@glide/common-core/dist/js/computation-model/data";
import { getTableName, sheetNameForTable } from "@glide/type-schema";
import {
    type ActionDescription,
    type MutatingScreenKind,
    type PropertyDescription,
    ActionKind,
    getStringProperty,
    getSwitchProperty,
} from "@glide/app-description";
import type { InputOutputTables } from "@glide/common-core/dist/js/description";
import {
    type AppDescriptionContext,
    type PropertyDescriptor,
    type RewritingComponentConfiguratorContext,
    classScreenName,
    PropertySection,
    makeSingleRelationOrThisItemPropertyDescriptor,
    type ActionAvailability,
} from "@glide/function-utils";
import { nullToUndefined } from "@glide/support";
import {
    type WireValueGetterGeneric,
    type WireActionResult,
    type WireActionResultBuilder,
    PageScreenTarget,
    type WireActionHydrator,
    type WireActionInflationBackend,
} from "@glide/wire";
import { definedMap } from "@glideapps/ts-necessities";
import { getMenuScreenNames } from "../description-utils";
import type { StaticActionContext } from "../static-context";
import { makeRowGetter } from "../wire/utils";
import { type ActionDescriptor, ActionGroup } from "./action-descriptor";
import { type DescriptionToken, actionAvailabilityApps } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import {
    type NavigationTargetDescription,
    type SingleRelationOrThisDescription,
    getTableForSourceColumnOrThis,
    makeNavigationPropertyDescriptors,
    navigationTargetPropertyHandlers,
} from "./link-to-screen";
import { getSupportsUserProfileRowAccess } from "./set-columns";
import { ICON_PALE } from "../plugins/icon-colors";
import type { GlideIconProps } from "@glide/plugins-codecs";

interface PushDetailScreenActionDescription
    extends ActionDescription,
        SingleRelationOrThisDescription,
        NavigationTargetDescription {
    readonly kind: ActionKind.PushDetailScreen;

    readonly menuID: PropertyDescription | undefined;
    readonly isMenu: PropertyDescription | undefined;

    readonly title: PropertyDescription | undefined;

    readonly navigationTarget: PropertyDescription | undefined;
}

function getIsMenu(desc: PushDetailScreenActionDescription | undefined): boolean {
    return getSwitchProperty(desc?.isMenu) ?? false;
}

export class PushDetailScreenHandler extends BaseActionHandler<PushDetailScreenActionDescription> {
    public readonly kind = ActionKind.PushDetailScreen;

    public readonly iconName: GlideIconProps = {
        icon: "st-show-details",
        kind: "stroke",
        strokeFgColor: ICON_PALE,
    };

    public readonly name: string = "Show detail screen";

    public get canAutoRun(): boolean {
        return true;
    }

    public get availability(): ActionAvailability {
        return actionAvailabilityApps;
    }

    public getDescriptor(
        _desc: PushDetailScreenActionDescription | undefined,
        { context: ccc }: StaticActionContext<AppDescriptionContext>
    ): ActionDescriptor {
        const properties: PropertyDescriptor[] = [];
        properties.push(
            makeSingleRelationOrThisItemPropertyDescriptor(
                "sourceColumn",
                "Data",
                PropertySection.Source,
                false,
                getSupportsUserProfileRowAccess(ccc)
            )
        );

        properties.push(...makeNavigationPropertyDescriptors(PageScreenTarget.Current));

        return {
            name: this.name,
            group: ActionGroup.Interaction,
            groupItemOrder: 1,
            needsScreenContext: true,
            properties,
        };
    }

    public getScreensUsed(
        desc: PushDetailScreenActionDescription,
        env: StaticActionContext<AppDescriptionContext>
    ): readonly string[] {
        const { context: adc, tables } = env;

        if (getIsMenu(desc)) {
            const screens: string[] = [];

            if (tables !== undefined) {
                screens.push(classScreenName(getTableName(tables.input)));
            }

            if (adc.appDescription === undefined) return screens;

            const menuID = getStringProperty(desc.menuID);
            if (menuID === undefined) return screens;

            screens.push(...getMenuScreenNames(adc.appDescription, menuID));
            return screens;
        } else {
            const table = getTableForSourceColumnOrThis(desc.sourceColumn, true, true, env);
            if (table === undefined) return [];

            return [classScreenName(getTableName(table))];
        }
    }

    public rewriteAfterReload(
        desc: PushDetailScreenActionDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): PushDetailScreenActionDescription | undefined {
        if (getIsMenu(desc)) {
            const menuID = getStringProperty(desc.menuID);
            if (menuID === undefined) return undefined;

            if (!ccc.requireMenuScreen(menuID)) return undefined;
        }

        return super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind);
    }

    public getTokenizedDescription(
        desc: PushDetailScreenActionDescription,
        env: StaticActionContext<AppDescriptionContext>
    ): readonly DescriptionToken[] | undefined {
        const isMenu = getIsMenu(desc);
        if (isMenu) {
            return [{ kind: "string", value: "for menu" }];
        }

        const token = tokenForProperty(desc.sourceColumn, env);
        if (token !== undefined) {
            return [{ kind: "string", value: "for " }, token];
        }

        return [{ kind: "string", value: "for this item" }];
    }

    public inflate(
        ib: WireActionInflationBackend,
        desc: PushDetailScreenActionDescription,
        arbBase: WireActionResultBuilder
    ): WireActionHydrator | WireActionResult {
        let titleGetter: WireValueGetterGeneric<string> | undefined;

        const maybeGetter = makeRowGetter(ib, desc.sourceColumn, { inOutputRow: false, defaultToThisRow: true });
        if (maybeGetter === undefined || maybeGetter === false) return arbBase.inflationError("No screen data");
        const { rowGetter, table } = maybeGetter;
        const tableName = getTableName(table);

        const target = navigationTargetPropertyHandlers.get(PageScreenTarget.Current).getEnum(desc);

        return (vp, skipLoading) => {
            const arb = arbBase.addData({ tableName: sheetNameForTable(table), target });

            const rowValue = nullToUndefined(rowGetter(vp));
            if (isLoadingValue(rowValue)) return arb.maybeSkipLoading(skipLoading, "Data row");
            const row = definedMap(rowValue, asRow);
            if (row === undefined) return arb.error(true, "No row for screen");

            const maybeTitle = titleGetter?.(vp) ?? "";
            const title = maybeTitle === "" ? undefined : maybeTitle;
            // The RowActionHydrationValueProvider might include a reference to a context which contains references
            // to actions. The returned function below is saved in the actions of the _next_ RowActionHydrationValueProvider,
            // so if we don't pull the rowContext out now, we end up with a memory leak.
            // This is currently possible because WireRowHydrationValueProvider.rowContext is readonly.
            const rowContext = vp.rowContext;

            return async ab => {
                dispatchActivationProgressEvent("edit-details-page");
                ab.pushDefaultClassScreen(tableName, row, title, target, rowContext?.inputRows[0]?.$rowID);
                return arb.addData({ title, rowID: row.$rowID }).success();
            };
        };
    }
}
